// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import SetProductFilterText from "@conklin/App/Handlers/OrderHistory/SetProductFilterText";
import { CustomAppState } from "@conklin/App/Store/Reducers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import getAutocompleteModel from "@insite/client-framework/Store/CommonHandlers/GetAutocompleteModel";
import updateSearchFields from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SearchFieldWrapper, {
    SearchFieldWrapperStyles,
} from "@insite/content-library/Widgets/OrderHistory/SearchFieldWrapper";
import DynamicDropdown, { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import debounce from "lodash/debounce";
import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    getOrdersParameter: state.pages.orderHistory.getOrdersParameter,
    // XNG-Changes
    search: state.xProductFilterText.text,
});

const mapDispatchToProps = {
    getAutocompleteModel,
    updateSearchFields,
    SetProductFilterText,
};

export interface OrderHistorySearchFieldProductErpNumber2Styles {
    wrapper?: SearchFieldWrapperStyles;
    searchDynamicDropdown?: DynamicDropdownPresentationProps;
    optionWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    productImage?: LazyImageProps;
    infoWrapper?: InjectableCss;
    autocompleteTitleText?: TypographyPresentationProps;
    autocompleteErpText?: TypographyPresentationProps;
}

export const orderHistorySearchFieldProductErpNumber2Styles: OrderHistorySearchFieldProductErpNumber2Styles = {
    optionWrapper: {
        css: css`
            display: flex;
            cursor: pointer;
        `,
    },
    imageWrapper: {
        css: css`
            display: flex;
            width: 50px;
            height: 50px;
            margin-right: 10px;
        `,
    },
    productImage: {
        width: "50px",
        css: css`
            flex-shrink: 0;
            img {
                height: 100%;
            }
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    autocompleteTitleText: {
        size: 14,
    },
    autocompleteErpText: {
        size: 14,
        css: css`
            margin-top: 5px;
        `,
    },
};
const styles = orderHistorySearchFieldProductErpNumber2Styles;

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const OrderHistorySearchFieldProductErpNumber2 = ({
    getOrdersParameter,
    getAutocompleteModel,
    updateSearchFields,
    // XNG-Changes
    search,
    SetProductFilterText,
}: Props) => {
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        setOptions([]);
    }, [getOrdersParameter]);

    const onSelectionChangeHandler = (value?: string) => {
        // if (searchResults && searchResults.length > 0) {
        //     const variantParentId = searchResults.find(o => o.id === value)?.styleParentId;
        //     if (variantParentId) {
        //         setProduct({
        //             productId: variantParentId,
        //             variantId: value,
        //             validateProduct: true,
        //             skipInventoryValidation: true,
        //         });
        //     } else {
        //         setProduct({
        //             productId: value,
        //             validateProduct: true,
        //             skipInventoryValidation: true,
        //         });
        //     }
        // } else {
        updateSearchFields({ productErpNumber: value });
        // }
    };

    const debouncedSearchProducts = useCallback(
        debounce((query: string) => {
            if (!query) {
                setIsLoading(false);
                setOptions([]);
                return;
            }

            getAutocompleteModel({
                query,
                onSuccess: result => {
                    setIsLoading(false);

                    if (!result?.products || result.products.length === 0) {
                        const trimmedQuery = query.trim();
                        setOptions([
                            {
                                optionText: trimmedQuery,
                                optionValue: trimmedQuery,
                                rowChildren: <StyledWrapper {...styles.optionWrapper}>{trimmedQuery}</StyledWrapper>,
                            },
                        ]);
                        return;
                    }

                    const newOptions = result.products.map(product => ({
                        optionText: product.erpNumber,
                        optionValue: product.erpNumber || undefined,
                        rowChildren: (
                            <StyledWrapper {...styles.optionWrapper}>
                                <StyledWrapper {...styles.imageWrapper}>
                                    <LazyImage {...styles.productImage} src={product.image} />
                                </StyledWrapper>
                                <StyledWrapper {...styles.infoWrapper}>
                                    <Typography {...styles.autocompleteTitleText}>{product.title}</Typography>
                                    <Typography {...styles.autocompleteErpText}>{product.erpNumber}</Typography>
                                </StyledWrapper>
                            </StyledWrapper>
                        ),
                    }));
                    setOptions(newOptions);
                },
                onComplete(resultProps) {
                    if (resultProps?.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        this.onSuccess?.(resultProps?.apiResult);
                    }
                },
            });
        }, 300),
        [],
    );

    const onInputChanged = (event: ChangeEvent<HTMLInputElement>) => {
        setIsLoading(true);
        // XNG-Changes
        SetProductFilterText({ text: event.target.value });
        debouncedSearchProducts(event.target.value);
    };

    return (
        <SearchFieldWrapper extendedStyles={styles.wrapper}>
            <DynamicDropdown
                {...styles.searchDynamicDropdown}
                label={translate("Product")}
                onSelectionChange={onSelectionChangeHandler}
                onInputChange={onInputChanged}
                filterOption={() => true}
                selected={getOrdersParameter.productErpNumber}
                options={options}
                hideNoOptionsIfEmptySearch
                isLoading={isLoading}
                placeholder={translate("Enter keyword or item #")}
                data-test-selector="orderHistory_filterProduct"
                /**
                 * XNG-Changes
                 * OOTB issue: on press Clear Button, Clear the text from select product Input
                 */
                search={search}
            />
        </SearchFieldWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderHistorySearchFieldProductErpNumber2),
    definition: {
        group: "Order History",
        displayName: "Product ERP Number",
        allowedContexts: ["OrderHistoryPage", "VmiOrderHistoryPage"],
    },
};

export default widgetModule;
